import type { CSSProperties } from 'react'
import { DECK_FLOOR_INSET } from '../utils/deckPaneViewport'

/**
 * The deck screen's two-pane geometry: the shell, the list column with its scroll
 * region, and the bounded reading pane (DESIGN_SYSTEM.md §9, §11, §13; ADR-067).
 *
 * Moved here unchanged from DeckDesktopPanels so that the card panels and the
 * text panels (the deck's «Тексты», handoff §19.6) are laid out by the same
 * numbers and cannot drift apart. Only the layout lives here; nothing about cards.
 */

// Two panels side by side, each scrolling on its own, filling whatever box the
// page anchors them in. The height is viewport-derived rather than content-
// derived so the right panel genuinely stands still while the left scrolls
// (§22) — the alternative, letting the page scroll both, is the single-surface
// arrangement the phone uses and is what §22 replaces.
//
// It used to derive that height HERE, as `calc(100vh - 200px)`. The 200 was
// never measured against anything: the chrome above this shell is roughly 500px
// and grows with every optional row, so the shell began around 500 and ended
// well past the bottom of the window. `100%` instead of a calc of its own, so
// there is one place the number comes from and it is a measurement — see
// `useScrollportMetrics` and `utils/deckPaneViewport`. `minHeight` remains as the
// floor below which a card list is not worth showing, and the page's anchor
// applies the same floor to the box it gives us.
//
// `minHeight` is gone from here (2026-09-12): the shell no longer sizes itself
// at all. Its parent — the deck screen's own frame — is the one box measured
// against the viewport, and it applies the same floor (`PANE_MIN_HEIGHT`) once,
// to the band and the columns together. A second floor here would have fought
// it: on a short window the shell would hold 420 while the frame had less to
// give, and the difference would have hung below the frame with nothing able to
// scroll to it, since the page itself no longer scrolls.
export const panelsShellStyle: CSSProperties = {
  display: 'flex', gap: 'var(--deck-pane-gutter)', alignItems: 'stretch',
  height: '100%', minHeight: 0,
  // The containing block for the reference layer, which sits over the pane.
  position: 'relative',
}

// The LIST COLUMN — head and scroll region, stacked. It takes whatever the
// sized pane leaves (see `rightPanelWidth`), which is why this is `1 1 auto`
// with no basis of its own: two percentage bases would be a second opinion
// about the same split, and the one that lost would be the one the
// reading/editing widths are expressed in.
//
// `minHeight: 0` is load-bearing: a flex item's automatic minimum size is its
// content, so without it the scroller below would refuse to shrink past the
// full height of the deck and the column would push the frame open instead of
// scrolling inside it.
export const leftColumnStyle: CSSProperties = {
  flex: '1 1 auto', minWidth: 0, minHeight: 0,
  display: 'flex', flexDirection: 'column',
}

// The list's scroll region: one of the screen's two, and the only part of the
// list column that moves under the wheel. The column's own chrome — the 6px
// scroll inset and the 1px rule that closes it against the gutter — stays
// HERE rather than moving to the column above, so the 7px this declares — 13
// with the 6px scrollbar a scrolling deck adds — in the list floor derived in
// DESIGN_SYSTEM.md §9 is unchanged by the head landing above it. The head takes the same inset back as padding
// (`listHeadStyle`) so the two agree on where the column's content ends.
export const leftPanelStyle: CSSProperties = {
  flex: '1 1 auto', minHeight: 0, minWidth: 0, overflowY: 'auto', overflowX: 'hidden',
  paddingRight: 6,
  // No `paddingBottom` here: the list's floor inset is applied in the
  // component (`listFloorPadding`), because how much of it the padding has to
  // supply depends on whether a trailing `InsertDivider` already sits under
  // the last row. It used to be a flat 28 here, sized for that divider's room
  // rather than for the frame's floor.
  borderRight: '1px solid var(--border)',
}

// The sized column: `0 0 auto` so its own `width` (reading or editing — see
// `rightPanelWidth`) is what the flex layout honours, rather than a basis
// competing with it.
//
// A bounded surface (design pass, 2026-09-09), not bare content sitting
// directly on the page: `--bg-surface` / `--border` / `--radius-md` are the
// same trio the list's own rows take on hover/selection, so the pane reads
// as a bounded object standing BESIDE the list — the `--deck-pane-gutter`
// gap between them — rather than floating unboxed while its neighbour is
// not. Constant across the reading and editing states (both branches render
// inside this one style): a box that appeared only in one state would jump
// the moment the two swap, which is exactly what the pane's own transition
// is written to never do (see `rightPanelWidth`'s comment above).
// **The pane runs the full height of the columns region**, and that is a
// reversal of the 2026-09-10 erratum, which gave it `alignSelf: 'flex-start'`
// so a short card would not sit at the top of a tall box. §11.6 answers that
// case directly — "short cards are not centred or padded; leftover room is
// itself the signal" — and §13 makes the full height one of the four
// differences that hold the pane and a row apart ("the pane runs the full
// height of the content area and no row ever does"). It is also what the seam
// is measured by: the pane's top edge sits on the column-head line and its
// floor on the content floor, so it is taller than the list's SCROLL REGION by
// exactly the head plus its gap, whatever the card in it happens to be. A pane
// that shrank to its content would make that register depend on the card.
//
// `stretch` rather than `height: '100%'` for the same reason the list uses it:
// it is the shell's own default (`panelsShellStyle`'s `alignItems: 'stretch'`),
// and stated here so the reversal is visible at the site it reverses.
//
// The pane box is itself the scroller (there is no separate body scrollport),
// so the floor inset is its own bottom padding — on the scrolling content,
// never a shortened box (§13, "The floor"). Less the 1px border, so the last
// line scrolled to the end clears the frame's bottom line, which is the
// border's outer edge, by exactly the inset. In a pane whose content is
// shorter than its box the padding is inside the same surface, so it adds no
// band: the leftover room stays leftover room.
const PANE_BORDER_WIDTH = 1
export const rightPanelStyle: CSSProperties = {
  flex: '0 0 auto', alignSelf: 'stretch', minWidth: 0, minHeight: 0,
  overflowY: 'auto', position: 'relative',
  padding: 'var(--space-5) var(--space-6)',
  paddingBottom: DECK_FLOOR_INSET - PANE_BORDER_WIDTH,
  background: 'var(--bg-surface)', border: `${PANE_BORDER_WIDTH}px solid var(--border)`,
  borderRadius: 'var(--radius-md)',
}
